import ImagePreview from '@/components/forms/imagePreview';
import Input from '@/components/forms/Input';
import Textarea from '@/components/forms/Textarea';
import { Upload } from '@/components/upload';
import { landlineNumberRegex, nameValidationRegex } from '@/constants/regex';
import { useDynamicLocale } from '@/hoslog-intl';
import { optionalUploadValueSchema } from '@/lib/uploadSchema';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';

export const storeInfoSchema = z.object({
  coverImage: optionalUploadValueSchema,
  profileImage: optionalUploadValueSchema,

  storeName: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
  storeNameFurigana: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .regex(nameValidationRegex, 'common.form.validation.nameOnly'),

  groupName: z
    .string()
    .trim()
    .regex(nameValidationRegex, 'common.form.validation.nameOnly')
    .optional()
    .or(z.literal('')),

  landlineNumber: z
    .string()
    .min(1, { message: 'common.form.validation.required' })
    .refine((val) => landlineNumberRegex.test(val), {
      message: 'common.form.validation.invalidLandlineNumber',
    }),

  description: z.string().trim().min(1, 'common.form.validation.required'),
});

interface BasicStoreInfoProps {
  profileImagePath?: string | undefined;
  setProfileImage?: (image: string) => void;
  coverImagePath?: string | undefined;
  setCoverImage?: (image: string) => void;
}

export const StoreInfo: React.FC<BasicStoreInfoProps> = ({
  profileImagePath,
  setProfileImage,
  coverImagePath,
  setCoverImage,
}: BasicStoreInfoProps) => {
  const { register, control } = useFormContext();
  const { t } = useDynamicLocale();

  return (
    <div>
      <div className="relative">
        {coverImagePath ? (
          <ImagePreview
            imageClassName="w-full object-cover"
            imageSrc={`${coverImagePath}?v=${Date.now()}`}
            alt="coverImage"
            cancelClickHandler={() => setCoverImage?.('')}
          />
        ) : (
          <Controller
            name="coverImage"
            control={control}
            render={({ field, fieldState }) => (
              <Upload
                buttonText={t('configurationManagement.location.page.editModal.browseFile')}
                accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                multiple={false}
                maxSizeMB={5}
                previewClassName="h-48 w-full p-0"
                error={fieldState.error?.message}
                onFilesChange={(files) => {
                  return field.onChange(files?.[0] ?? '');
                }}
                dropZoneClassName="relative w-full inline-flex items-center justify-center bg-surface-subtle min-h-52"
              />
            )}
          />
        )}
        {profileImagePath ? (
          <ImagePreview
            className="absolute top-23 left-10"
            variant="circle"
            imageClassName="rounded-full"
            imageSrc={`${profileImagePath}?v=${Date.now()}`}
            alt="profileImage"
            cancelClickHandler={() => setProfileImage?.('')}
          />
        ) : (
          <Controller
            name="profileImage"
            control={control}
            render={({ field, fieldState }) => (
              <Upload
                accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                multiple={false}
                error={fieldState.error?.message}
                maxSizeMB={5}
                onFilesChange={(files) => {
                  return field.onChange(files?.[0] ?? '');
                }}
                variant="circle"
                className="absolute bottom-0 left-[48px] translate-y-1/2"
                showPlaceholderText={false}
              />
            )}
          />
        )}
      </div>

      <div className="flex gap-5 sm:gap-10 md:gap-15 lg:gap-20">
        <div className="w-[200px] xl:w-[245px] shrink-0"></div>
        <div className="gap-4 w-full mt-6">
          <div className="flex flex-col md:grid md:grid-cols-2 gap-4 w-full">
            <Input
              label="storeManagement.form.storeInfo.label.storeName"
              placeholder={t('storeManagement.storeInfo.storeNamePlaceholder')}
              maxLength={50}
              {...register('storeName')}
            />
            <Input
              label="storeManagement.form.storeInfo.label.furigana"
              placeholder={t('storeManagement.storeInfo.furiganaPlaceholder')}
              maxLength={50}
              {...register('storeNameFurigana')}
            />
            <Input
              label="storeManagement.form.storeInfo.label.groupName"
              placeholder={t('storeManagement.storeInfo.groupNamePlaceholder')}
              maxLength={50}
              {...register('groupName')}
            />
            <Controller
              name="landlineNumber"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  inputMode="numeric"
                  maxLength={11}
                  label="storeManagement.form.storeInfo.label.telephoneNumber"
                  placeholder="0312345678"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const cleaned = e.target.value.replace(/[^0-9]/g, '');

                    if (cleaned.length > 11) return;

                    field.onChange(cleaned);
                  }}
                />
              )}
            />
          </div>
          <div className="mt-4">
            <Textarea
              label="storeManagement.form.storeInfo.label.description"
              placeholder={t('common.form.placeholder.description')}
              isRequired
              className="resize-none"
              maxLength={200}
              {...register('description')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
